import type { UserTime } from '~/lib/model';

/**
 * ARRIVAL WINDOWS (founder, 9/26/26). Customers pick from these times and
 * nothing else: All day, or one of three overlapping windows. The windows have
 * the same hours as Bleenk's schedule card, so an order reads the same
 * whichever way it came in.
 *
 * A window is stored as a UserTime `between` and "All day" as a `day`. The
 * scheduler and every older order already understand both, so the older
 * before/after/specific-time requests still display (usertime_format) but can
 * no longer be picked.
 */
export const ARRIVAL_WINDOWS = [
  { id: 'morning', name: 'Morning', start: '09:00', end: '12:00' },
  { id: 'midday', name: 'Midday', start: '11:00', end: '14:00' },
  { id: 'afternoon', name: 'Afternoon', start: '13:00', end: '16:00' },
] as const satisfies ReadonlyArray<{ id: string; name: string; start: TTime24; end: TTime24 }>;

export type ArrivalWindow = (typeof ARRIVAL_WINDOWS)[number];

export type ArrivalWindowId = ArrivalWindow['id'];

/** What a customer picks for one day. */
export type ArrivalChoice = 'day' | ArrivalWindowId;

export interface ArrivalHours {
  start: TTime24;
  end: TTime24;
}

/**
 * What "All day" means when the company has not set its own hours. It is
 * separate from `schedule_start`/`schedule_end`: those hours bound the office's
 * schedule board and run late for twilight, which is not something to promise
 * a customer.
 */
export const DEFAULT_ARRIVAL_HOURS: ArrivalHours = { start: '09:00', end: '16:00' };

/** The company's "All day" hours (`company.arrival_start`/`arrival_end`), or the default. */
export function arrivalHours(company?: { arrival_start?: string; arrival_end?: string }): ArrivalHours {
  const start = company?.arrival_start;
  const end = company?.arrival_end;

  // Zero-padded 24-hour clocks compare correctly as strings. A reversed pair
  // is a typo, and showing it would promise a customer nothing.
  return start && end && start < end ? { start: start as TTime24, end: end as TTime24 } : DEFAULT_ARRIVAL_HOURS;
}

export function arrivalWindow(id: ArrivalWindowId): ArrivalWindow {
  return ARRIVAL_WINDOWS.find((window) => window.id === id)!;
}

/** '09:00' -> '9 AM', '12:30' -> '12:30 PM'. */
export function clockLabel(time: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(time ?? '');

  // Stored data is not always tidy; show it as it is rather than throw.
  if (!match) {
    return time ?? '';
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const suffix = hours >= 12 && hours < 24 ? 'PM' : 'AM';
  const hour = hours % 12 === 0 ? 12 : hours % 12;

  return minutes ? `${hour}:${String(minutes).padStart(2, '0')} ${suffix}` : `${hour} ${suffix}`;
}

/** '9 AM – 4 PM' */
export function hoursLabel(hours: { start: string; end: string }): string {
  return `${clockLabel(hours.start)} – ${clockLabel(hours.end)}`;
}

/** The window a stored request is, if it is exactly one. */
export function arrivalWindowOf(time: { type?: string; start?: string; end?: string }): ArrivalWindow | undefined {
  if (time.type !== 'between') {
    return undefined;
  }

  return ARRIVAL_WINDOWS.find((window) => window.start === time.start && window.end === time.end);
}

/** The choice a stored request is. Undefined for older requests a customer can no longer pick. */
export function arrivalChoiceOf(time: { type?: string; start?: string; end?: string }): ArrivalChoice | undefined {
  if (time.type === 'day') {
    return 'day';
  }

  return arrivalWindowOf(time)?.id;
}

/** The picker's options, each labeled with its hours. */
export function arrivalChoices(hours: ArrivalHours): Array<{ value: ArrivalChoice; label: string }> {
  return [
    { value: 'day', label: `All day (${hoursLabel(hours)})` },
    ...ARRIVAL_WINDOWS.map((window) => ({ value: window.id, label: `${window.name} (${hoursLabel(window)})` })),
  ];
}

export function isArrivalChoice(value: string): value is ArrivalChoice {
  return value === 'day' || ARRIVAL_WINDOWS.some((window) => window.id === value);
}

/** The UserTime fields a choice is stored as (the `day` is the caller's). */
export function arrivalFields(
  choice: ArrivalChoice,
): Pick<Extract<UserTime, { type: 'day' }>, 'type'> | Pick<Extract<UserTime, { type: 'between' }>, 'type' | 'start' | 'end'> {
  if (choice === 'day') {
    return { type: 'day' };
  }

  const window = arrivalWindow(choice);

  return { type: 'between', start: window.start, end: window.end };
}
